<template>
  <SidebarComponent />

  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Questionari senza utente</h1>
          <p class="muted">
            Mostra gli ultimi {{ fetchLimit }} questionari/check con uid mancante/non valido e un eventuale candidato recuperato dal link o dal percorso.
          </p>
        </div>

        <section class="card-surface toolbar-card">
          <div class="toolbar">
            <div class="toolbar-group toolbar-group--sm">
              <label class="toolbar-label">Ultimo controllo</label>
              <div class="mono">{{ lastScanLabel }}</div>
            </div>
            <div class="toolbar-actions">
              <button class="btn primary staff-button-neutral" type="button" @click="loadIssues" :disabled="loading">
                {{ loading ? 'Carico…' : 'Ricarica' }}
              </button>
            </div>
          </div>
        </section>

        <div v-if="loading" class="loader"><h4>Caricamento…</h4></div>
        <div v-else-if="error" class="puq-state puq-state--error">{{ error }}</div>
        <div v-else-if="!rows.length" class="puq-state">Nessun questionario problematico trovato nel range.</div>
        <div v-else class="tickets">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Doc ID</th>
                <th>UID originale</th>
                <th>UID candidato</th>
                <th>Fonte</th>
                <th>Percorso</th>
                <th>Step</th>
                <th>Data</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in rows" :key="row.id">
                <td class="mono">{{ idx + 1 }}</td>
                <td class="mono">{{ row.id }}</td>
                <td class="mono">
                  <div>{{ row.rawUid || '—' }}</div>
                  <small v-if="row.sanitizedUid && row.sanitizedUid !== row.rawUid" class="muted">
                    Sanificato: {{ row.sanitizedUid }}
                  </small>
                </td>
                <td class="mono">
                  <span v-if="row.candidateUid">{{ row.candidateUid }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td>{{ row.candidateSource || '—' }}</td>
                <td>
                  <div class="mono">{{ row.percorsoId || '—' }}</div>
                  <small v-if="row.percorsoUid" class="muted">UID percorso: {{ row.percorsoUid }}</small>
                </td>
                <td>
                  <div>{{ row.stepKey || '—' }}</div>
                  <small v-if="row.stepIndex !== null" class="muted">index: {{ row.stepIndex }}</small>
                </td>
                <td>
                  <div>{{ formatDate(row.updatedAt || row.createdAt) || '—' }}</div>
                </td>
                <td>
                  <button
                    class="btn primary staff-button-positive"
                    type="button"
                    :disabled="!row.candidateUid || fixing[row.id]"
                    @click="applyFix(row)"
                  >
                    {{ fixing[row.id] ? 'Salvo…' : 'Applica UID' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import SidebarComponent from '@/components/SidebarComponent.vue';

const FETCH_LIMIT = 300;

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const sanitizeUid = (raw) => {
  if (raw === undefined || raw === null) return '';
  const str = String(raw);
  let decoded = str;
  try {
    decoded = decodeURIComponent(str);
  } catch (err) {
    // ignore malformed URI sequences
  }
  return decoded.trim().replace(/[^\w-]/g, '');
};

export default {
  name: 'QuestionariUidFix',
  components: { SidebarComponent },
  data() {
    return {
      db: getFirestore(),
      rows: [],
      loading: false,
      error: '',
      fixing: {},
      userCache: {},
      percorsoCache: {},
      lastScan: null,
      fetchLimit: FETCH_LIMIT,
    };
  },
  computed: {
    lastScanLabel() {
      if (!this.lastScan) return '—';
      return this.formatDate(this.lastScan);
    },
  },
  mounted() {
    this.loadIssues();
  },
  methods: {
    formatDate(value) {
      const date = toDateSafe(value);
      if (!date) return '';
      return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
    async userExists(uid) {
      if (!uid) return false;
      if (Object.prototype.hasOwnProperty.call(this.userCache, uid)) {
        return this.userCache[uid];
      }
      try {
        const snap = await getDoc(doc(this.db, 'users', uid));
        const exists = snap.exists();
        this.userCache[uid] = exists;
        return exists;
      } catch (err) {
        console.warn('userExists error', uid, err);
        this.userCache[uid] = false;
        return false;
      }
    },
    async fetchPercorsoMeta(percorsoId) {
      if (!percorsoId) return null;
      if (this.percorsoCache[percorsoId]) return this.percorsoCache[percorsoId];
      try {
        const snap = await getDoc(doc(this.db, 'percorsi', percorsoId));
        const data = snap.exists() ? snap.data() || {} : {};
        const meta = { uid: sanitizeUid(data.uid), steps: Array.isArray(data.steps) ? data.steps : [] };
        this.percorsoCache[percorsoId] = meta;
        return meta;
      } catch (err) {
        console.warn('fetchPercorsoMeta error', percorsoId, err);
        this.percorsoCache[percorsoId] = null;
        return null;
      }
    },
    async loadIssues() {
      this.loading = true;
      this.error = '';
      this.rows = [];
      this.userCache = {};
      this.percorsoCache = {};
      try {
        const q = query(
          collection(this.db, 'questionari'),
          orderBy('createdAt', 'desc'),
          limit(this.fetchLimit),
        );
        const snap = await getDocs(q);
        const nextRows = [];
        for (const docSnap of snap.docs) {
          const data = docSnap.data() || {};
          const rawUid = data.uid || '';
          const sanitizedUid = sanitizeUid(rawUid);
          const rawMatchesSanitized = rawUid === sanitizedUid;
          const sanitizedExists = sanitizedUid && await this.userExists(sanitizedUid);
          const hasValidUid = rawMatchesSanitized && sanitizedExists;
          if (hasValidUid) continue;

          let candidateUid = '';
          let candidateSource = '';
          let percorsoUid = '';

          if (sanitizedUid && (!rawMatchesSanitized || !sanitizedExists) && await this.userExists(sanitizedUid)) {
            candidateUid = sanitizedUid;
            candidateSource = 'UID dal link sanificato';
          }

          const percorsoId = data.percorsoId || '';
          if (!candidateUid && percorsoId) {
            const meta = await this.fetchPercorsoMeta(percorsoId);
            percorsoUid = sanitizeUid(meta?.uid);
            if (percorsoUid && await this.userExists(percorsoUid)) {
              candidateUid = percorsoUid;
              candidateSource = 'UID dal percorso';
            }
          } else if (percorsoId) {
            const meta = await this.fetchPercorsoMeta(percorsoId);
            percorsoUid = sanitizeUid(meta?.uid);
          }

          nextRows.push({
            id: docSnap.id,
            rawUid,
            sanitizedUid,
            candidateUid,
            candidateSource,
            percorsoId,
            percorsoUid,
            stepKey: data.stepKey || data.type || '',
            stepIndex: Number.isInteger(data.stepIndex) ? data.stepIndex : null,
            createdAt: toDateSafe(data.createdAt),
            updatedAt: toDateSafe(data.updatedAt || data.createdAt),
          });
        }
        this.rows = nextRows;
        this.lastScan = new Date();
      } catch (err) {
        console.error('loadIssues error', err);
        this.error = 'Errore nel recupero dei questionari.';
      } finally {
        this.loading = false;
      }
    },
    async applyFix(row) {
      if (!row?.candidateUid) return;
      this.fixing = { ...this.fixing, [row.id]: true };
      try {
        await updateDoc(doc(this.db, 'questionari', row.id), { uid: row.candidateUid });

        // Allinea lo step nel percorso, se presente
        if (row.percorsoId && Number.isInteger(row.stepIndex)) {
          try {
            const percorsoRef = doc(this.db, 'percorsi', row.percorsoId);
            const snap = await getDoc(percorsoRef);
            if (snap.exists()) {
              const data = snap.data() || {};
              const steps = Array.isArray(data.steps) ? [...data.steps] : [];
              const idx = row.stepIndex;
              if (steps[idx]) {
                const prev = steps[idx];
                steps[idx] = {
                  ...prev,
                  questionarioDocId: row.id,
                  questionnaire: {
                    ...(prev.questionnaire || {}),
                    id: row.id,
                    questionarioUid: prev.questionnaire?.questionarioUid || row.id,
                    uid: row.candidateUid,
                  },
                };
                await updateDoc(percorsoRef, { steps });
              }
            }
          } catch (stepErr) {
            console.warn('Impossibile aggiornare lo step del percorso', stepErr);
          }
        }

        this.rows = this.rows.filter((r) => r.id !== row.id);
      } catch (err) {
        console.error('applyFix error', err);
        alert('Errore durante l\'aggiornamento dell\'UID.');
      } finally {
        const nextFixing = { ...this.fixing };
        delete nextFixing[row.id];
        this.fixing = nextFixing;
      }
    },
  },
};
</script>
